import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, BookOpen, Calendar, ShoppingCart, Sun, Moon, Plus, ChefHat, Check, History, X, Dices, Megaphone, Bell } from "lucide-react";
import { toast } from "sonner";
import { api, recipeImage } from "@/lib/api";
import { todayStr, weekRange, prettyDate } from "@/lib/dates";
import AddMealDialog from "@/components/AddMealDialog";
import { useAuth } from "@/context/AuthContext";
import { pushSupported, enablePush } from "@/lib/push";

const QUICK = [
  { to: "/scan", label: "Scanner", icon: Camera, bg: "var(--at-brand-light)", fg: "var(--at-brand)" },
  { to: "/recipes", label: "Mes recettes", icon: BookOpen, bg: "var(--at-sage-light)", fg: "var(--at-sage)" },
  { to: "/week", label: "Ma semaine", icon: Calendar, bg: "var(--at-honey-light)", fg: "var(--at-honey)" },
  { to: "/shopping", label: "Mes courses", icon: ShoppingCart, bg: "#EEF0FA", fg: "#4A4E7A" },
];

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [today, setToday] = useState([]);
  const [week, setWeek] = useState([]);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [tonightLoading, setTonightLoading] = useState(false);
  const [announcement, setAnnouncement] = useState(null);
  const [annDismissedVer, setAnnDismissedVer] = useState(
    () => localStorage.getItem("atable_announcement_dismissed") || ""
  );
  const dismissAnnouncement = () => {
    if (!announcement) return;
    localStorage.setItem("atable_announcement_dismissed", announcement.updated_at || "");
    setAnnDismissedVer(announcement.updated_at || "");
  };

  // Notifications : ré-abonnement silencieux si déjà autorisé + invite 1 tap sinon
  const [notifPromptDismissed, setNotifPromptDismissed] = useState(
    () => localStorage.getItem("atable_notif_prompt_dismissed") === "1"
  );
  const canPromptNotif =
    pushSupported() && typeof Notification !== "undefined" && Notification.permission === "default";
  useEffect(() => {
    if (pushSupported() && typeof Notification !== "undefined" && Notification.permission === "granted") {
      enablePush().catch(() => {});
    }
  }, []);
  const dismissNotifPrompt = () => {
    localStorage.setItem("atable_notif_prompt_dismissed", "1");
    setNotifPromptDismissed(true);
  };
  const enableNotifs = async () => {
    try {
      await enablePush();
      toast.success("Notifications activées ✅");
      dismissNotifPrompt();
    } catch (e) {
      if (e.message === "denied") toast.error("Autorisez les notifications pour recevoir les annonces");
      else toast.error("Impossible d'activer les notifications");
      dismissNotifPrompt();
    }
  };
  const [reminderDismissed, setReminderDismissed] = useState(
    () => localStorage.getItem("atable_dinner_reminder_" + todayStr()) === "1"
  );
  const dismissReminder = () => {
    localStorage.setItem("atable_dinner_reminder_" + todayStr(), "1");
    setReminderDismissed(true);
  };

  const load = () => {
    const { start, end } = weekRange();
    api.get(`/meals?start=${start}&end=${end}`).then((r) => {
      setWeek(r.data);
      setToday(r.data.filter((m) => m.date === todayStr()));
    });
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => { api.get("/announcement").then((r) => setAnnouncement(r.data)).catch(() => {}); }, []);

  const upcoming = week
    .filter((m) => m.date > todayStr())
    .sort((a, b) => (a.date + a.slot).localeCompare(b.date + b.slot))
    .slice(0, 6);

  const slotMeal = (s) => today.find((m) => m.slot === s);

  const markCooked = async (m) => {
    await api.post("/history", { recipe_id: m.recipe.id, servings: m.servings, slot: m.slot });
    toast.success("Bravo ! Ajouté à l'historique 🎉");
  };

  const pickTonight = async () => {
    setTonightLoading(true);
    try {
      const { data } = await api.post("/meals/tonight", { date: todayStr() });
      if (data.already) {
        toast.info(`Un dîner est déjà prévu ce soir : ${data.meal?.recipe?.title || "…"}`);
      } else {
        toast.success(`Ce soir on cuisine : ${data.meal?.recipe?.title} ✨`);
        load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aucune recette disponible pour ce soir");
    } finally {
      setTonightLoading(false);
    }
  };

  const MealSlot = ({ slot, icon: Icon, label }) => {
    const m = slotMeal(slot);
    return (
      <div className="flex-1 rounded-2xl border border-black/5 bg-white p-3 at-shadow" data-testid={`today-${slot}`}>
        <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--at-muted)" }}>
          <Icon size={14} /> {label}
        </div>
        {m && m.recipe ? (
          <div>
            <button onClick={() => navigate(`/recipes/${m.recipe.id}`)} className="w-full text-left at-press">
              <div className="mb-2 aspect-video w-full overflow-hidden rounded-xl" style={{ background: "var(--at-elevated)" }}>
                {recipeImage(m.recipe) ? <img src={recipeImage(m.recipe)} alt="" className="h-full w-full object-contain" />
                  : <div className="flex h-full items-center justify-center"><ChefHat size={22} style={{ color: "var(--at-muted)" }} /></div>}
              </div>
              <p className="text-sm font-semibold leading-tight line-clamp-2">{m.recipe.title}</p>
            </button>
            <button data-testid={`cooked-${slot}-btn`} onClick={() => markCooked(m)}
              className="mt-2 flex w-full items-center justify-center gap-1 rounded-lg py-1.5 text-xs font-semibold at-press"
              style={{ background: "var(--at-sage-light)", color: "var(--at-sage)" }}>
              <Check size={13} /> J'ai cuisiné
            </button>
          </div>
        ) : (
          <button onClick={() => setDlgOpen(true)} data-testid={`add-${slot}-btn`}
            className="flex h-[104px] w-full flex-col items-center justify-center gap-1 rounded-xl border border-dashed at-press"
            style={{ borderColor: "var(--at-border)", color: "var(--at-muted)" }}>
            <Plus size={20} /> <span className="text-xs">Ajouter</span>
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="px-5 pt-8" data-testid="home-page">
      <header className="flex items-start justify-between at-fade-up">
        <div>
          <p className="text-sm font-medium" style={{ color: "var(--at-sage)" }}>À Table</p>
          <h1 className="font-display text-3xl font-bold leading-tight sm:text-4xl">Qu'est-ce qu'on mange&nbsp;?</h1>
          <p className="mt-1 text-sm" style={{ color: "var(--at-text-2)" }}>{prettyDate(todayStr())}</p>
        </div>
        <button data-testid="profile-btn" onClick={() => navigate("/more")}
          className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full text-white at-shadow at-press" style={{ background: "var(--at-brand)" }}>
          {user?.picture && /^(https?:|\/)/.test(user.picture) ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user?.name?.[0]?.toUpperCase() || "?")}
        </button>
      </header>

      {/* Annonce du super admin */}
      {announcement && announcement.message && (announcement.updated_at || "") !== annDismissedVer && (
        <div className="mt-5 flex items-start gap-3 rounded-2xl p-4 at-shadow at-fade-up" style={{ background: "var(--at-sage-light)", border: "1px solid var(--at-sage)" }} data-testid="announcement-banner">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl" style={{ background: "var(--at-sage)", color: "#fff" }}><Megaphone size={18} /></span>
          <p className="min-w-0 flex-1 whitespace-pre-line text-sm font-medium leading-snug" style={{ color: "var(--at-text)" }} data-testid="announcement-text">{announcement.message}</p>
          <button data-testid="announcement-dismiss" onClick={dismissAnnouncement} aria-label="Masquer l'annonce" className="shrink-0 rounded-full p-1 at-press" style={{ color: "var(--at-sage)" }}><X size={18} /></button>
        </div>
      )}

      {/* Invite à activer les notifications (1 tap) */}
      {canPromptNotif && !notifPromptDismissed && (
        <div className="mt-5 flex items-center gap-3 rounded-2xl p-4 at-shadow at-fade-up" style={{ background: "var(--at-honey-light)", border: "1px solid var(--at-honey)" }} data-testid="notif-prompt">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl" style={{ background: "var(--at-honey)", color: "#fff" }}><Bell size={20} /></span>
          <div className="min-w-0 flex-1">
            <p className="font-display text-base font-bold leading-tight">Activer les notifications</p>
            <p className="text-xs" style={{ color: "var(--at-text-2)" }}>Pour recevoir le rappel du dîner et les annonces de la famille.</p>
          </div>
          <button data-testid="notif-prompt-enable" onClick={enableNotifs} className="shrink-0 rounded-xl px-3 py-2 text-sm font-semibold text-white at-press" style={{ background: "var(--at-honey)" }}>Activer</button>
          <button data-testid="notif-prompt-dismiss" onClick={dismissNotifPrompt} aria-label="Plus tard" className="shrink-0 rounded-full p-1 at-press" style={{ color: "var(--at-honey)" }}><X size={18} /></button>
        </div>
      )}

      {/* Rappel du dîner (fin de journée) */}
      {(() => {
        const dinner = slotMeal("soir");
        if (!dinner || !dinner.recipe || reminderDismissed || new Date().getHours() < 16) return null;
        return (
          <div className="mt-5 flex items-center gap-3 rounded-2xl p-4 at-shadow at-fade-up" style={{ background: "var(--at-brand)", color: "#fff" }} data-testid="dinner-reminder">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/20"><Moon size={22} /></span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ opacity: 0.9 }}>Ce soir on cuisine…</p>
              <p className="truncate font-display text-lg font-bold" data-testid="dinner-reminder-title">{dinner.recipe.title}</p>
            </div>
            <button data-testid="dinner-reminder-open" onClick={() => navigate(`/recipes/${dinner.recipe.id}`)} className="shrink-0 rounded-xl bg-white/20 px-3 py-2 text-sm font-semibold at-press">Voir</button>
            <button data-testid="dinner-reminder-dismiss" onClick={dismissReminder} aria-label="Masquer" className="shrink-0 rounded-full p-1 at-press"><X size={18} /></button>
          </div>
        );
      })()}

      {/* Repas du jour */}
      <section className="mt-6 at-fade-up" style={{ animationDelay: "60ms" }}>
        <h2 className="mb-3 font-display text-xl font-semibold">Aujourd'hui</h2>
        <button data-testid="tonight-btn" onClick={pickTonight} disabled={tonightLoading}
          className="mb-3 flex w-full items-center justify-center gap-2 rounded-2xl py-3.5 text-sm font-semibold text-white at-press at-shadow disabled:opacity-60"
          style={{ background: "var(--at-brand)" }}>
          <Moon size={18} /> {tonightLoading ? "On cherche…" : "Menu du soir — surprends-moi"} <Dices size={18} />
        </button>
        <div className="flex gap-3">
          <MealSlot slot="midi" icon={Sun} label="Midi" />
          <MealSlot slot="soir" icon={Moon} label="Soir" />
        </div>
      </section>

      {/* Actions rapides */}
      <section className="mt-7 grid grid-cols-2 gap-3 at-fade-up" style={{ animationDelay: "120ms" }}>
        {QUICK.map((q) => {
          const Icon = q.icon;
          return (
            <button key={q.to} data-testid={`quick-${q.label}`} onClick={() => navigate(q.to)}
              className="at-press flex items-center gap-3 rounded-2xl border border-black/5 bg-white p-4 at-shadow">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: q.bg, color: q.fg }}>
                <Icon size={22} />
              </span>
              <span className="text-left font-semibold leading-tight">{q.label}</span>
            </button>
          );
        })}
      </section>

      {/* Prochains repas */}
      <section className="mt-7 at-fade-up" style={{ animationDelay: "180ms" }}>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold">Prochains repas</h2>
          <span className="rounded-full px-3 py-1 text-xs font-semibold" style={{ background: "var(--at-sage-light)", color: "var(--at-sage)" }}>
            {week.length} cette semaine
          </span>
        </div>
        {upcoming.length === 0 ? (
          <div className="rounded-2xl border border-dashed p-6 text-center text-sm" style={{ borderColor: "var(--at-border)", color: "var(--at-muted)" }}>
            Aucun repas prévu. <button onClick={() => setDlgOpen(true)} className="font-semibold underline" style={{ color: "var(--at-brand)" }}>Planifier</button>
          </div>
        ) : (
          <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
            {upcoming.map((m) => (
              <button key={m.id} onClick={() => m.recipe && navigate(`/recipes/${m.recipe.id}`)} data-testid={`upcoming-${m.id}`}
                className="at-press w-40 shrink-0 overflow-hidden rounded-2xl border border-black/5 bg-white text-left at-shadow">
                <div className="aspect-video w-full overflow-hidden" style={{ background: "var(--at-elevated)" }}>
                  {m.recipe && recipeImage(m.recipe) ? <img src={recipeImage(m.recipe)} alt="" className="h-full w-full object-contain" /> : null}
                </div>
                <div className="p-2.5">
                  <p className="text-[11px] font-semibold uppercase" style={{ color: "var(--at-brand)" }}>{prettyDate(m.date).split(" ").slice(0, 1)} · {m.slot}</p>
                  <p className="text-sm font-medium leading-tight line-clamp-2">{m.recipe?.title || "Recette"}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Accès historique */}
      <button data-testid="home-history-link" onClick={() => navigate("/history")}
        className="mt-5 flex w-full items-center gap-3 rounded-2xl border border-black/5 bg-white p-4 at-shadow at-press at-fade-up" style={{ animationDelay: "220ms" }}>
        <span className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: "var(--at-brand-light)", color: "var(--at-brand)" }}>
          <History size={22} />
        </span>
        <span className="text-left">
          <span className="block font-semibold leading-tight">Historique & favoris</span>
          <span className="block text-xs" style={{ color: "var(--at-muted)" }}>Les plats préférés de la famille</span>
        </span>
      </button>

      <AddMealDialog open={dlgOpen} onOpenChange={setDlgOpen} onAdded={load} />
    </div>
  );
}
